"use client";

import Image from "next/image";
import React, { useMemo } from "react";
import Slider from "react-slick";
import styled from "styled-components";

import { ArrowIcon, Text, TextSize, TextWeight } from "../../components";
import { useMatchBreakpoints } from "../../hooks";
// Types
import type { ProductGroup } from "../../types";
import type { Product } from "../../types/product.type";
import { ProductMiniature } from "../ProductMiniature";

type ProductsGroupsProps = {
  productGroups: ProductGroup[];
  useCarousel?: boolean;
};

export const ProductsGroups: React.FC<ProductsGroupsProps> = ({
  productGroups,
  useCarousel = false,
}) => {
  const { isXs, isSm, isMd } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd;

  const baseUrl = useMemo(
    () => process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost/prestashop",
    [],
  );

  const getSliderSettings = (productCount: number) => {
    return {
      dots: false,
      infinite: productCount > 4,
      speed: 500,
      slidesToShow: Math.min(4, productCount),
      slidesToScroll: 1,
      arrows: productCount > 4,
      autoplay: false,
      prevArrow: <CustomArrow direction="prev" />,
      nextArrow: <CustomArrow direction="next" />,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: Math.min(3, productCount),
            slidesToScroll: 1,
            infinite: productCount > 3,
            arrows: productCount > 3,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, productCount),
            slidesToScroll: 1,
            infinite: productCount > 2,
            arrows: productCount > 2,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: productCount > 1,
            arrows: productCount > 1,
          },
        },
      ],
    };
  };

  // Custom arrow component
  const CustomArrow = ({
    onClick,
    direction,
  }: {
    onClick?: () => void;
    direction: "prev" | "next";
  }) => (
    <button
      className={`slick-${direction}`}
      onClick={onClick}
      aria-label={`${direction === "prev" ? "Previous" : "Next"} slide`}
    >
      <ArrowIcon />
    </button>
  );

  const getImagePath = (productGroup: ProductGroup) => {
    const imagePath = isMobile
      ? productGroup.imagePathMobile
      : productGroup.imagePathDesktop;

    return `${baseUrl}${imagePath}`;
  };

  // Funkcja do renderowania produktów
  const renderProductsCarousel = (products: Product[]) => {
    const shouldUseCarousel =
      useCarousel || products.length > 4 || isXs || isSm;
    const sliderSettings = getSliderSettings(products.length);

    if (!shouldUseCarousel) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2 mt-10">
          {products.map((product, index) => (
            <ProductMiniature
              key={`${product.id}-${index}`}
              product={product as Product}
            />
          ))}
        </div>
      );
    }

    return (
      <ProductsCarouselWrapper>
        <div className="products-slider-container">
          <div className="products-slider">
            <Slider {...sliderSettings}>
              {products.map((product, index) => (
                <div key={`${product.id}-${index}`} className="product-slide">
                  <ProductMiniature product={product as Product} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </ProductsCarouselWrapper>
    );
  };

  if (!productGroups || productGroups.length === 0) {
    return null;
  }

  return (
    <div className="w-full max-w-screen-2xl mx-auto">
      {productGroups.map((productGroup) => (
        <div key={productGroup.idProductGroup} className="container-break">
          <div className="flex flex-col justify-center my-10">
            <ProductGroupBanner>
              <BannerImage
                src={getImagePath(productGroup)}
                alt={productGroup.title}
                fill
                className="product_group_image"
                priority
              />

              <ProductsGroupsContent>
                <GroupTitle titleColor={productGroup.titleColor || undefined}>
                  {productGroup.title}
                </GroupTitle>

                {productGroup.buttonText && (
                  <ButtonsArea>
                    <ProductGroupButton
                      href={productGroup.buttonUrl.replace(
                        "https://carry.pl",
                        "",
                      )}
                      backgroundColor={productGroup.buttonBackgroundColor}
                      textColor={productGroup.buttonTextColor}
                      borderColor={productGroup.buttonBorderColor}
                    >
                      <Text
                        text={productGroup.buttonText}
                        textTransform="uppercase"
                        className="tracking-widest"
                        weight={TextWeight.BOLD}
                        fontSize={TextSize.S}
                      />
                    </ProductGroupButton>
                  </ButtonsArea>
                )}
              </ProductsGroupsContent>
            </ProductGroupBanner>

            {productGroup.products &&
              productGroup.products.length > 0 &&
              renderProductsCarousel(productGroup.products as Product[])}
          </div>
        </div>
      ))}
    </div>
  );
};

// Styled components pozostają bez zmian
const ProductsCarouselWrapper = styled.div`
  width: 100%;
  margin-top: 2.5rem;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 100vw;
  }

  .products-slider-container {
    width: 100%;
    position: relative;

    .products-slider {
      height: auto;

      .product-slide {
        padding: 0 5px;
        height: 100%;

        > div {
          height: 100%;
        }
      }
    }
  }

  .slick-list {
    overflow: hidden;
    width: 100% !important;
  }

  .slick-track {
    display: flex;
    align-items: stretch;
  }

  .slick-slide {
    > div {
      height: 100%;
    }
  }

  .slick-next,
  .slick-prev {
    position: absolute;
    z-index: 1;
    width: 30px;
    height: 60px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    background: none;
    border: none;
    outline: none;
    display: block;
    fill: #000;

    svg {
      width: 30px;
      height: 60px;
      fill: #000;
    }

    &:before {
      display: none;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .slick-prev {
    left: 5px;
  }

  .slick-next {
    right: 5px;

    svg {
      transform: rotate(180deg);
    }
  }
`;

const ProductGroupBanner = styled.div`
  position: relative;
  height: 600px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;
`;

const BannerImage = styled(Image)`
  position: absolute;
  object-fit: cover;
  object-position: center top;
  height: 100%;
  width: 100%;
`;

const ProductsGroupsContent = styled.div`
  width: 100%;
  z-index: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 60%;
  }

  @media (max-width: 576px) {
    width: 70%;
  }
`;

const GroupTitle = styled.h2<{ titleColor?: string }>`
  font-size: 45px;
  font-weight: 700;
  line-height: 57px;
  letter-spacing: 0.1em;
  text-align: center;
  color: ${(props) => props.titleColor || "white"};
  text-transform: uppercase;
`;

const ButtonsArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 40px 0 20px;

  @media (max-width: 576px) {
    .mobile-line {
      width: 100%;
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;
    }
  }
`;

const ProductGroupButton = styled.a<{
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
}>`
  width: 155px;
  height: 50px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 15px;
  font-family: NunitoSans-Bold, serif;
  letter-spacing: 0.3em;
  text-align: center;
  text-transform: uppercase;
  color: ${(props) => props.textColor || "black"};
  border: 1px solid ${(props) => props.borderColor || "black"};
  background-color: ${(props) => props.backgroundColor || "white"};
  padding-left: 0.3em;
  padding-top: 1px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 576px) {
    width: 120px;
    height: 37px;
    font-size: 10px;
    line-height: 14px;
  }
`;

export default ProductsGroups;
